/**
 * Normalize Valhalla trip or Mapbox Directions-shaped route into a common form
 * for traffic enrichment. Distances are meters; times are seconds.
 */

import { decodeShape } from './polyline'
import { haversineMeters } from './densifyRoutePoints'

const METERS_PER_MILE = 1609.344

/**
 * Detect input format.
 * @param {object} input Valhalla trip, Mapbox route, or Mapbox directions response
 * @returns {'valhalla'|'mapbox'|null}
 */
export function detectTripFormat(input) {
  if (!input || typeof input !== 'object') {
    return null
  }
  // Mapbox Directions response
  if (Array.isArray(input.routes) && input.routes.length > 0) {
    return 'mapbox'
  }
  // Single Mapbox route
  if (
    Array.isArray(input.legs) &&
    input.legs.length > 0 &&
    (input.geometry?.coordinates ||
      input.legs.some((leg) => Array.isArray(leg.steps)))
  ) {
    const hasValhallaShape = input.legs.some(
      (leg) => typeof leg.shape === 'string'
    )
    const hasMapboxSteps = input.legs.some((leg) => Array.isArray(leg.steps))
    if (hasMapboxSteps && !hasValhallaShape) {
      return 'mapbox'
    }
  }
  // Valhalla trip
  if (Array.isArray(input.legs) && input.legs.length > 0) {
    if (
      typeof input.legs[0].shape === 'string' ||
      Array.isArray(input.legs[0].maneuvers)
    ) {
      return 'valhalla'
    }
  }
  // Wrapped { trip: {...} }
  if (input.trip) {
    return detectTripFormat(input.trip)
  }
  return null
}

function lengthToMeters(length, units) {
  if (length == null || Number.isNaN(length)) {
    return null
  }
  if (units === 'kilometers' || units === 'km') {
    return length * 1000
  }
  if (units === 'meters' || units === 'm') {
    return length
  }
  // Valhalla default in this app: miles
  return length * METERS_PER_MILE
}

function geometryFromLonLat(coords) {
  return (coords || []).map(([lon, lat], shapeIndex) => ({
    lon,
    lat,
    shapeIndex,
  }))
}

function polylineLengthM(geometry) {
  let sum = 0
  for (let i = 0; i < geometry.length - 1; i++) {
    sum += haversineMeters(geometry[i], geometry[i + 1])
  }
  return sum
}

/**
 * Build leg geometry + maneuvers from Mapbox steps.
 */
function normalizeMapboxLeg(leg) {
  const steps = Array.isArray(leg.steps) ? leg.steps : []
  const coords = []
  const maneuvers = []

  for (const step of steps) {
    const stepCoords = step.geometry?.coordinates
    if (!Array.isArray(stepCoords) || stepCoords.length === 0) {
      continue
    }
    const begin = coords.length === 0 ? 0 : Math.max(0, coords.length - 1)
    if (coords.length === 0) {
      for (const coord of stepCoords) {
        coords.push([coord[0], coord[1]])
      }
    } else {
      for (let i = 1; i < stepCoords.length; i++) {
        coords.push([stepCoords[i][0], stepCoords[i][1]])
      }
    }
    const end = coords.length - 1
    maneuvers.push({
      begin_shape_index: begin,
      end_shape_index: end,
      time: step.duration ?? null,
      lengthM: step.distance ?? null,
      length: step.distance != null ? step.distance / METERS_PER_MILE : null,
      instruction: step.maneuver?.instruction || step.name || '',
      type: step.maneuver?.type,
    })
  }

  // Fallback: whole-leg geometry if no steps
  if (coords.length === 0 && Array.isArray(leg.geometry?.coordinates)) {
    for (const coord of leg.geometry.coordinates) {
      coords.push([coord[0], coord[1]])
    }
  }

  const geometry = geometryFromLonLat(coords)
  const distanceM =
    leg.distance != null ? leg.distance : polylineLengthM(geometry)
  const durationSec = leg.duration != null ? leg.duration : null

  return {
    durationSec,
    distanceM,
    geometry,
    coordinates: coords,
    coordinateCount: coords.length,
    maneuvers,
    raw: leg,
  }
}

function normalizeValhallaLeg(leg, units) {
  let lonLat = decodeShape(leg.shape)
  if (
    lonLat.length === 0 &&
    Array.isArray(leg.coordinates) &&
    leg.coordinates.length > 0
  ) {
    lonLat = decodeShape(leg.coordinates)
  }
  if (
    lonLat.length === 0 &&
    Array.isArray(leg.geometry) &&
    leg.geometry.length > 0
  ) {
    lonLat = leg.geometry.map((point) =>
      Array.isArray(point) ? [point[0], point[1]] : [point.lon, point.lat]
    )
  }
  const geometry = geometryFromLonLat(lonLat)
  const maneuvers = (leg.maneuvers || []).map((mnv) => ({
    begin_shape_index: mnv.begin_shape_index,
    end_shape_index: mnv.end_shape_index,
    time: mnv.time ?? null,
    lengthM: lengthToMeters(mnv.length, units),
    length: mnv.length ?? null,
    instruction: mnv.instruction || '',
    type: mnv.type,
  }))

  const distanceM =
    lengthToMeters(leg.summary?.length, units) ?? polylineLengthM(geometry)
  const durationSec = leg.summary?.time ?? null

  return {
    durationSec,
    distanceM,
    geometry,
    coordinates: lonLat,
    coordinateCount: lonLat.length,
    maneuvers,
    raw: leg,
  }
}

/**
 * @param {object} input Valhalla trip | Mapbox route | { routes } | { trip }
 * @param {{ routeIndex?: number }} [options]
 * @returns {{
 *   ok: boolean,
 *   format?: 'valhalla'|'mapbox',
 *   error?: string,
 *   durationSec?: number,
 *   distanceM?: number,
 *   units?: string,
 *   legs?: object[],
 *   uuid?: string|null,
 *   original?: object,
 *   mapboxRoute?: object|null,
 * }}
 */
export function normalizeTrip(input, options = {}) {
  if (!input) {
    return { ok: false, error: 'missing_trip' }
  }

  let root = input
  let format = detectTripFormat(root)
  let mapboxRoute = null
  let uuid = null
  let original = input

  if (format === null && root.trip) {
    root = root.trip
    format = detectTripFormat(root)
    original = root
  }

  if (format === 'mapbox') {
    if (Array.isArray(root.routes)) {
      uuid = root.uuid || null
      const idx = options.routeIndex ?? 0
      mapboxRoute = root.routes[idx]
      if (!mapboxRoute) {
        return { ok: false, error: 'no_mapbox_route', format }
      }
      original = mapboxRoute
    } else {
      mapboxRoute = root
      original = root
    }

    const legs = (mapboxRoute.legs || []).map((leg) => normalizeMapboxLeg(leg))
    if (!legs.length) {
      return { ok: false, error: 'no_legs', format }
    }

    return {
      ok: true,
      format: 'mapbox',
      units: 'meters',
      durationSec: mapboxRoute.duration ?? null,
      distanceM: mapboxRoute.distance ?? null,
      legs,
      uuid,
      original,
      mapboxRoute,
      mapboxResponse: Array.isArray(input.routes) ? input : null,
    }
  }

  if (format === 'valhalla') {
    const units = root.units || 'miles'
    const legs = (root.legs || []).map((leg) =>
      normalizeValhallaLeg(leg, units)
    )
    if (!legs.length) {
      return { ok: false, error: 'no_legs', format }
    }

    return {
      ok: true,
      format: 'valhalla',
      units,
      durationSec: root.summary?.time ?? null,
      distanceM: lengthToMeters(root.summary?.length, units),
      legs,
      uuid: null,
      original: root,
      mapboxRoute: null,
      mapboxResponse: null,
    }
  }

  return { ok: false, error: 'unrecognized_trip_format' }
}

export { METERS_PER_MILE, lengthToMeters }
